import { Stack } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import { getEffortLabel, getResourceType } from "../utils";
import EffortTag from "../components/effortTag/EffortTag";
import type { OpportunityDetailResponse } from "../types";
import { CommentsSection } from "./CommentsSection";
import { DetailCard } from "./DetailCard";
import { DetailRow } from "./DetailRow";
import { DetailResources } from "./DetailResources";
import StatusPicker from "./StatusPicker";
import OwnerPicker from "../components/ownerPicker";

interface OpportunityInformationProps {
  data: OpportunityDetailResponse;
  assignedStatus: string;
  setAssignedStatus: (s: string) => void;
  assignedOwner: string;
  setAssignedOwner: (o: string) => void;
}

// Information tab body for the opportunity detail page — the 1.55fr/1fr
// grid (Description + Resources on the left, Details sidebar on the right)
// and the Comments card below. Extracted from OpportunityDetailPage to keep
// each file under the 250-line cap. Matches the "Discover (A)" design.
export const OpportunityInformation = ({
  data,
  assignedStatus,
  setAssignedStatus,
  assignedOwner,
  setAssignedOwner,
}: OpportunityInformationProps) => {
  const { datastores = [] } = useAppState();

  const effortLabel = getEffortLabel(data.effort);
  const resourceTypeLabel = getResourceType(data.resource_type);
  const instanceName = datastores.find(d => Number(d.instance_id) === data.instance_id)?.name;
  const detections = data.count != null ? data.count.toLocaleString() : "—";
  // Guard against malformed date strings — `new Date("bad").toISOString()` throws.
  const toDate = (v?: string | null) => {
    if (!v) return undefined;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? undefined : d;
  };
  const startDateObj = toDate(data.first_seen);
  const endDateObj = toDate(data.last_seen);
  const startDate = startDateObj?.toISOString();
  const endDate = endDateObj?.toISOString();

  return (
    <Stack direction="column" gap="md">
      <div
        style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 18, alignItems: "start" }}
      >
        {/* left: description + resources */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {data.description && (
            <DetailCard title="Description">
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "#475467" }}>
                {data.description}
              </p>
            </DetailCard>
          )}
          {data.resources && data.resources.length > 0 && (
            <DetailCard title={`Resources (${data.resources.length})`} pad="14px 20px">
              <DetailResources resources={data.resources} resourceTypeLabel={resourceTypeLabel} />
            </DetailCard>
          )}
        </div>

        {/* right: details sidebar */}
        <DetailCard title="Details" pad="6px 20px 12px">
          <DetailRow
            label="Status"
            value={
              <StatusPicker
                opportunity_id={data.opportunity_id}
                assignedStatus={assignedStatus}
                setAssignedStatus={setAssignedStatus}
                startDate={startDate}
                endDate={endDate}
              />
            }
          />
          <DetailRow
            label="Owner"
            value={
              <OwnerPicker
                opportunity_id={data.opportunity_id}
                assignedOwner={assignedOwner}
                setAssignedOwner={setAssignedOwner}
                startDate={startDate}
                endDate={endDate}
              />
            }
          />
          <DetailRow label="Effort" value={<EffortTag effort={effortLabel} />} />
          <DetailRow label="Opportunity type" value={resourceTypeLabel || "—"} />
          <DetailRow label="Instance" value={instanceName || "—"} />
          <DetailRow label="Primary resource" value={data.resource_name || "—"} />
          <DetailRow label="Detections" value={detections} last />
        </DetailCard>
      </div>

      <CommentsSection opportunity_id={data.opportunity_id} instance_id={data.instance_id} />
    </Stack>
  );
};
